import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Loader2, X, PenLine } from "lucide-react";
import { Stars, StarInput } from "@/components/StarRating";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Reveal } from "@/lib/motion";
import { imgSrc } from "@/lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", rating: 0, title: "", body: "", photo: null };

export const ProductReviews = ({ productId, productName }) => {
  const { user } = useCustomerAuth();
  const [data, setData] = useState({ summary: { avg: 0, count: 0, breakdown: {} }, reviews: [] });
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/products/${productId}/reviews`).then(({ data }) => setData(data)).catch(() => {});
  }, [productId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.name) setForm((f) => (f.name ? f : { ...f, name: user.name })); }, [user]);

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/reviews/upload`, fd);
      setForm((f) => ({ ...f, photo: data.url }));
    } catch {
      toast.error("Could not upload photo. Please try a smaller JPG or PNG.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Please pick a star rating");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, { ...form, product_id: productId });
      toast.success(data.message || "Thank you for your review!");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your review");
    } finally {
      setBusy(false);
    }
  };

  const { summary, reviews } = data;

  return (
    <section data-testid="product-reviews" className="mt-20 sm:mt-28 border-t border-forest/10 pt-14">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="eyebrow mb-3">Ratings & Reviews</p>
          {summary.count > 0 ? (
            <div className="flex items-center gap-4">
              <span className="font-serif text-5xl text-forest">{summary.avg}</span>
              <div>
                <Stars value={summary.avg} size={18} />
                <p className="text-xs text-mutedgreen mt-1">Based on {summary.count} verified {summary.count === 1 ? "review" : "reviews"}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-mutedgreen max-w-md">Be the first to share your experience with {productName}.</p>
          )}
        </div>
        <button data-testid="write-review-button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.16em] px-6 py-3 rounded-full hover:bg-gold transition-colors">
          <PenLine size={14} /> Write a review
        </button>
      </div>

      {summary.count > 0 && (
        <div className="grid sm:grid-cols-[220px_1fr] gap-8 items-start mb-12">
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((s) => {
              const c = summary.breakdown?.[String(s)] || 0;
              const pct = summary.count ? (c / summary.count) * 100 : 0;
              return (
                <div key={s} className="flex items-center gap-2 text-xs text-mutedgreen">
                  <span className="w-3 text-forest">{s}</span>
                  <div className="flex-1 h-2 rounded-full bg-forest/10 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${pct}%` }} /></div>
                  <span className="w-6 text-right">{c}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {open && (
        <Reveal>
          <form data-testid="review-form" onSubmit={submit} className="bg-white border border-forest/10 rounded-2xl p-6 sm:p-8 mb-12 max-w-2xl">
            <p className="font-serif text-2xl text-forest mb-1">Share your thoughts</p>
            <p className="text-xs text-mutedgreen mb-6">Your review will appear after our team approves it.</p>
            <div className="mb-5">
              <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-forest/70 mb-2">Your rating</span>
              <StarInput value={form.rating} onChange={(r) => setForm((f) => ({ ...f, rating: r }))} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <input data-testid="review-name-input" value={form.name} onChange={on("name")} placeholder="Your name" required className="bg-cream border border-forest/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-gold" />
              <input data-testid="review-title-input" value={form.title} onChange={on("title")} placeholder="Review title (optional)" className="bg-cream border border-forest/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-gold" />
            </div>
            <textarea data-testid="review-body-input" value={form.body} onChange={on("body")} placeholder="What did you like? How did you use it?" rows={4} required className="w-full bg-cream border border-forest/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-gold mb-4" />
            <div className="flex items-center gap-4 mb-6">
              <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="review-photo-input" />
              {form.photo ? (
                <div className="relative">
                  <img src={imgSrc(form.photo)} alt="Your upload" className="w-20 h-20 rounded-xl object-cover border border-forest/15" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photo: null }))} className="absolute -top-2 -right-2 bg-forest text-cream rounded-full p-1"><X size={12} /></button>
                </div>
              ) : (
                <button type="button" data-testid="review-photo-button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-xs font-semibold text-forest border border-dashed border-forest/30 rounded-xl px-4 py-3 hover:border-gold hover:text-gold transition-colors">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Add a photo (optional)
                </button>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="text-xs font-bold uppercase tracking-[0.14em] text-forest border border-forest/20 px-5 py-2.5 rounded-full hover:bg-forest/5">Cancel</button>
              <button type="submit" data-testid="review-submit-button" disabled={busy} className="inline-flex items-center gap-2 bg-forest text-cream text-xs font-bold uppercase tracking-[0.14em] px-6 py-2.5 rounded-full hover:bg-gold transition-colors disabled:opacity-60">
                {busy && <Loader2 size={13} className="animate-spin" />} Submit review
              </button>
            </div>
          </form>
        </Reveal>
      )}

      {reviews.length > 0 && (
        <div className="space-y-6 max-w-3xl" data-testid="review-list">
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-forest/10 pb-6 last:border-0">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-forest/10 text-forest font-serif flex items-center justify-center text-sm">{r.name.charAt(0).toUpperCase()}</span>
                  <div>
                    <p className="text-sm font-semibold text-forest">{r.name}</p>
                    <Stars value={r.rating} size={13} />
                  </div>
                </div>
                <span className="text-[11px] text-mutedgreen">{new Date(r.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</span>
              </div>
              {r.title && <p className="text-sm font-semibold text-forest mt-3">{r.title}</p>}
              <p className="text-sm text-mutedgreen leading-relaxed mt-1.5">{r.body}</p>
              {r.photo && <img src={imgSrc(r.photo)} alt="" className="mt-3 w-24 h-24 rounded-xl object-cover border border-forest/10" />}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
